<script lang="ts">
  import { imgSrc } from '#app/lib/image_source'
  import { logError } from '#app/lib/loggers'
  import { onChange } from '#app/lib/svelte/svelte'
  import { getLocalDay } from '#app/lib/time'
  import { isContentLinkClick } from '#app/lib/user_content'
  import { isOpenedOutside, loadInternalLink } from '#app/lib/utils'
  import type { ClickEvent } from '#app/types/declarations/svelte'
  import ImagesCollage from '#components/images_collage.svelte'
  import Modal from '#components/modal.svelte'
  import Spinner from '#components/spinner.svelte'
  import type { SerializedEntity } from '#entities/lib/entities'
  import CommentTextPreview from '#listings/components/comment_text_preview.svelte'
  import ListingElementShow from '#listings/components/listing_element_show.svelte'
  import ListingElementsCount from '#listings/components/listing_elements_count.svelte'
  import { getListingPathname, getElementPathname, type SerializedListingWithMatchingElementsAndInfos } from '#listings/lib/listings'
  import type { ListingElement } from '#server/types/element'
  import type { EntityUri } from '#server/types/entity'
  import { i18n } from '#user/lib/i18n'
  import { getSerializedUser } from '#users/users_data'

  const {
    listing = $bindable(),
    uri,
    entity,
    onRemove,
  }: {
    listing: SerializedListingWithMatchingElementsAndInfos
    uri: EntityUri
    entity: SerializedEntity
    onRemove: (element: ListingElement) => void
  } = $props()

  let element = $derived(listing.elements.find(el => el.uri === uri))
  const comment = $derived(element?.comment)
  const elementPathname = $derived(element ? getElementPathname(listing._id, element._id) : undefined)
  const listingPathname = $derived(getListingPathname(listing._id))

  const imagesLimit = 6

  const waitingForUserdata = $derived(getSerializedUser(listing.creator))

  let showElementModal = $state(false)

  function showListingElementWithComment (e: ClickEvent) {
    if (isOpenedOutside(e) || isContentLinkClick(e)) return
    showElementModal = true
    e.preventDefault()
  }

  let username = $state('')

  $effect(() => {
    onChange(waitingForUserdata, () => {
      waitingForUserdata
        .then(user => {
          username = user.username
        })
        .catch(logError)
    })
  })

  const title = $derived.by(() => {
    const userContext = username ? i18n('Comment by %{username}', { username }) + '\n—\n' : ''
    if (comment) {
      if (comment.length > 1000) return userContext + comment.substring(0, 990) + '…'
      else return userContext + comment
    } else {
      return userContext
    }
  })

  function afterRemoveElement (element: ListingElement) {
    showElementModal = false
    onRemove(element)
  }
</script>

{#if element && elementPathname && comment}
  <li class="entity-listing-comment">
    <div class="comment-row">
      {#await waitingForUserdata}
        <Spinner />
      {:then creator}
        <div class="top">
          <a
            href={creator.pathname}
            onclick={loadInternalLink}
          >
            <img
              class="profile-pic"
              alt="{creator.username} avatar"
              src={imgSrc(creator.picture, 36)}
            />
            <span class="username">{creator.username}</span>
          </a>
          <span class="created">{getLocalDay(element.created)}</span>
        </div>
        <a
          href={elementPathname}
          onclick={showListingElementWithComment}
          {title}
        >
          <CommentTextPreview {comment} />
        </a>
      {/await}
    </div>

    <a
      href={listingPathname}
      class="listing-row"
      onclick={loadInternalLink}
    >
      <div class="listing-name-section">
        <div class="listing-name-label">
          {i18n('In list:')}
        </div>
        <span
          class="listing-name"
          // Display a title on hover for names likely to be overflowing
          title={listing.name.length > 70 ? listing.name : undefined}
        >{listing.name}</span>
      </div>
      <ImagesCollage
        imagesUrls={listing.imagesSample.slice(0, 4)}
        limit={imagesLimit}
        imageSize={128}
      />
      <ListingElementsCount {listing} />
    </a>
  </li>
{/if}

{#if showElementModal && element}
  {#await waitingForUserdata then creator}
    <Modal onCloseModal={() => showElementModal = false}>
      <ListingElementShow
        {entity}
        {creator}
        bind:element
        showListingContext={true}
        {listing}
        {afterRemoveElement}
      />
    </Modal>
  {/await}
{/if}

<style lang="scss">
  @import "general/scss/utils";

  .entity-listing-comment{
    @include display-flex(column);
    overflow-y: auto;
    background-color: white;
    @include shy-border;
    @include radius;
    &:hover, &:focus{
      background-color: darken(white, 1%);
    }
  }
  .top{
    @include display-flex(row, center, space-between);
  }
  .created{
    color: $grey;
  }
  .comment-row{
    @include display-flex(column);
    padding: 1rem 1rem 0.5rem;
  }
  .listing-name-section{
    padding-block-end: 0.5em;
    padding-inline-end: 0.5em;
    flex: 1 1 0;
  }
  .listing-name-label{
    opacity: 0.6;
    padding-block-start: 0.5em;
  }
  .listing-name{
    @include serif;
    display: block;
    font-weight: bold;
    max-height: 2.6rem;
    line-height: 1.2rem;
    overflow: hidden;
  }
  .listing-row{
    margin-block-start: auto;
    @include display-flex(row, center);
    padding-inline-start: 1rem;
    background-color: $light-grey;
    position: relative;
    @include radius;
    :global(.images-collage){
      block-size: 5em;
      background-color: $light-grey;
      max-width: 50%;
      flex: 1 1 0;
      justify-content: flex-end;
    }
    :global(.images-collage img){
      flex: 0 0 auto;
    }
    &:hover, &:focus{
      opacity: 0.8;
      @include transition(opacity);
    }
  }
  .profile-pic{
    @include radius;
    margin-inline-end: 0.5rem;
  }
</style>
